/**
 * Cliente HTTP para los datos REALES de `apps/api`.
 *
 * ⛔ **CORREGIDO 2026-09-05, y es la SEGUNDA correccion de esta misma cabecera. Vale leer las dos,
 * porque el patron se repite: lo que estaba escrito aca engano a CUATRO PRs la vez pasada.**
 *
 * Lo que decia hasta hoy: *"la consola usa `auth-jj` … lo que NO esta verificado por efecto es el
 * round-trip completo cookie->guard->userId contra un `auth-jj` REAL"*. **La consola YA NO usa
 * `auth-jj`** (`docs/06` §13): usa el login PROPIO de `taxi`, y `apps/api` sirve y lee su cookie.
 *
 * **Por que cambio, en una linea:** `auth-jj` no tiene registro de clientes ni concepto de
 * `issuer`/`audience`, asi que su cookie —de dominio `.argusapps.net`, que el navegador manda
 * sola— no podia distinguir a ESTA app de cualquier otra del portafolio.
 *
 * 🔑 **LO QUE NO CAMBIA, y es el invariante de este archivo: la consola nunca guarda ni manda un
 * token.** `credentials: 'include'` sigue siendo su UNICA via de autenticacion (invariante escrito
 * en `sesion/rutas.ts`). Lo que cambia es quien emite la cookie —este backend— y su alcance: es
 * del HOST exacto de la API (prefijo `__Host-`, sin `Domain=`) y no la recibe ninguna otra app.
 *
 * ⚠️ **DOS COSAS NUEVAS QUE HAY QUE SABER ANTES DE AGREGAR UNA LLAMADA MUTANTE POR ACA:**
 *  1. **Toda mutacion por cookie exige un `Origin` declarado en `CORS_ORIGENES`** del backend
 *     (defensa de CSRF, `docs/06` §13.4). El navegador lo manda solo; un `curl` no, y por eso un
 *     `curl` mutante con la cookie da **403 `origen_no_permitido`** — que NO es un problema de
 *     sesion aunque se le parezca.
 *  2. Un **403** ya no significa una sola cosa: puede ser falta de rol (`operador_requerido`) o
 *     ese chequeo de origen. `ErrorApi` trae el `code`, y hay que mirarlo antes de decirle a
 *     alguien que pida permisos.
 *
 * ⚠️ **Grado de verificacion, declarado:** el backend esta probado con HTTP real y controles
 * negativos (`apps/api/src/auth/sesion.integration.spec.ts`) y la sesion de esta app tambien
 * (`src/sesion/sesion.spec.tsx`). **Nadie abrio un navegador**: el round-trip real de la cookie
 * —incluido `http://localhost` en desarrollo, donde el prefijo `__Host-` exige `Secure`— sigue
 * SIN medir.
 *
 * 📌 **Por que esto se corrige en el CODIGO y no solo en el bus:** la version anterior de esta
 * cabecera quedo stale y **cuatro PRs declararon, cada uno por su cuenta, que sus pantallas
 * "darian 401 contra el backend real"** — ninguno lo midio, los cuatro leyeron esto. Un residuo
 * dentro de un archivo vivo hereda la credibilidad del archivo, y este se lee antes de tocar
 * cualquier pantalla.
 */
import { entorno } from '../config/entorno';

/**
 * Error de una llamada a `apps/api`. Trae el `status` HTTP crudo porque distintas pantallas
 * necesitan reaccionar distinto a un 401 (sesión) que a un 403 (permiso) que a un 400 (dato
 * inválido) — colapsar todo a un string pierde esa distinción.
 */
export class ErrorApi extends Error {
  readonly status: number;
  /**
   * `string | undefined`, NO `codigo?: string` — con `exactOptionalPropertyTypes` (activo en
   * este proyecto) esa forma no deja asignar `undefined` explícito, y acá el valor SIEMPRE se
   * asigna (a veces con `undefined` adentro).
   */
  readonly codigo: string | undefined;

  constructor(status: number, message: string, codigo: string | undefined = undefined) {
    super(message);
    this.name = 'ErrorApi';
    this.status = status;
    this.codigo = codigo;
  }
}

/**
 * `apps/api` devuelve DOS formas de cuerpo de error distintas, medidas por efecto:
 *  - `ValidationPipe` / `HttpException` genérica: `{statusCode, message, error}`, con `message`
 *    STRING o ARRAY de strings (class-validator manda un array cuando falla más de una regla).
 *  - Los guards de auth propios (`JwtAuthGuard`): `{message, code}`, sin `statusCode`.
 * Y al menos un caso (la URL de archivo sin almacén configurado) devuelve un 500 sin más detalle
 * que `{statusCode:500,message:"Internal server error"}` — Nest esconde el error real a
 * propósito. Ninguna de las cuatro formas puede asumirse: se intenta leer todas antes de caer al
 * mensaje genérico con el status crudo.
 */
async function mensajeDeError(
  respuesta: Response,
): Promise<{ mensaje: string; codigo: string | undefined }> {
  try {
    const cuerpo: unknown = await respuesta.json();
    if (cuerpo && typeof cuerpo === 'object') {
      const registro = cuerpo as Record<string, unknown>;
      const bruto = registro.message;
      const mensaje = Array.isArray(bruto)
        ? bruto.filter((m) => typeof m === 'string').join('. ')
        : typeof bruto === 'string'
          ? bruto
          : typeof registro.error === 'string'
            ? registro.error
            : null;
      const codigo = typeof registro.code === 'string' ? registro.code : undefined;
      if (mensaje) return { mensaje, codigo };
    }
  } catch {
    // El cuerpo no era JSON (o estaba vacío). Cae al mensaje genérico de abajo.
  }
  return { mensaje: `El backend respondió ${respuesta.status}.`, codigo: undefined };
}

export interface OpcionesPedido extends Omit<RequestInit, 'body' | 'credentials'> {
  /** Se serializa con `JSON.stringify`. Ausente = sin cuerpo (los `POST` sin DTO, p.ej. aprobar expediente). */
  cuerpo?: unknown;
}

/**
 * `fetch` contra `apps/api`, con las convenciones de la consola: `credentials: 'include'` (la
 * ÚNICA forma de auth que este cliente usa — ver el comentario de arriba), `Accept: application/
 * json`, y errores tipados en vez de un `Response` que cada pantalla tiene que inspeccionar.
 *
 * ⚠️ Sin `entorno.apiUrl` (modo "datos de ejemplo") esto NO debería llamarse — las pantallas que
 * lo usan tienen que gatear con `hayBackend` antes, igual que ya hace `Acceso.tsx`.
 */
export async function pedir<T>(ruta: string, opciones: OpcionesPedido = {}): Promise<T> {
  const { cuerpo, headers, ...resto } = opciones;

  const respuesta = await fetch(`${entorno.apiUrl}${ruta}`, {
    ...resto,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(cuerpo !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    // `body` NO se incluye en absoluto cuando no hay cuerpo — con `exactOptionalPropertyTypes`,
    // `body: undefined` explícito no satisface `BodyInit | null` aunque la clave sea opcional.
    ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
  });

  if (!respuesta.ok) {
    const { mensaje, codigo } = await mensajeDeError(respuesta);
    throw new ErrorApi(respuesta.status, mensaje, codigo);
  }

  if (respuesta.status === 204) return undefined as T;

  // Un `200`/`201` sin cuerpo (no debería pasar en estos 8 endpoints, pero un `.json()` sobre
  // una respuesta vacía tira `SyntaxError` — mejor un `undefined` tipado que una excepción rara.
  const texto = await respuesta.text();
  return (texto ? JSON.parse(texto) : undefined) as T;
}

/** Atajo para los `GET`. */
export const obtener = <T>(ruta: string): Promise<T> => pedir<T>(ruta);

/** Atajo para los `POST` con o sin cuerpo. */
export const postear = <T>(ruta: string, cuerpo?: unknown): Promise<T> =>
  pedir<T>(ruta, { method: 'POST', cuerpo });
